import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Plus, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../../../shared/ui/ConfirmDialog';
import ProtectedView from '../../auth/components/ProtectedView';
import { canManageCompanies } from '../../../utils/permissions';
import { API_CONFIG } from '../../../config/constants';
import type { Company, Sucursal } from '@/types';
import { isSucursalesSupported, markSucursalesUnsupported } from '../../../shared/services/sucursalesSupport';

type Props = {
  companies: Company[];
  addCompany: (c: Partial<Company>) => void;
  updateCompany: (id: string | number, c: Partial<Company>) => void;
  deleteCompany: (id: string | number) => void;
};

const CompaniesView: React.FC<Props> = ({ companies, addCompany, updateCompany, deleteCompany }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Company | undefined>(undefined);
  const [form, setForm] = useState<Partial<Company>>({ name: '', cuit: '', status: 'active' });
  
  const [expandedCompany, setExpandedCompany] = useState<Company['id'] | null>(null);
  const [showSucursalForm, setShowSucursalForm] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<Company['id'] | null>(null);
  const [sucursalForm, setSucursalForm] = useState({ name: '' });
  const [companySucursales, setCompanySucursales] = useState<Record<string, Sucursal[]>>({});
  const [sucursalesSupported, setSucursalesSupported] = useState(isSucursalesSupported());
  const [confirmDeleteCompany, setConfirmDeleteCompany] = useState<{ isOpen: boolean; companyId: string | number | null }>({ isOpen: false, companyId: null });
  const [confirmDeleteSucursal, setConfirmDeleteSucursal] = useState<{ isOpen: boolean; companyId: Company['id'] | null; sucursalId: Sucursal['id'] | null }>({ isOpen: false, companyId: null, sucursalId: null });

  const openNew = () => { setEditing(undefined); setForm({ name: '', cuit: '', status: 'active' }); setShowForm(true); };
  const openEdit = (c: Company) => { setEditing(c); setForm(c); setShowForm(true); };

  // Fetch sucursales when company is expanded
  useEffect(() => {
    if (expandedCompany !== null && !companySucursales[expandedCompany]) {
      fetchSucursales(expandedCompany);
    }
  }, [expandedCompany]);

  const fetchSucursales = async (companyId: Company['id']) => {
    if (!isSucursalesSupported()) {
      setSucursalesSupported(false);
      setCompanySucursales(prev => ({ ...prev, [String(companyId)]: [] }));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const companyIdParam = String(companyId);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/sucursales?empresaId=${companyIdParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 404) {
        markSucursalesUnsupported();
        setSucursalesSupported(false);
        setCompanySucursales(prev => ({ ...prev, [companyIdParam]: [] }));
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setCompanySucursales(prev => ({ ...prev, [companyIdParam]: data }));
      }
    } catch (error) {
      console.error('Error fetching sucursales:', error);
      setCompanySucursales(prev => ({ ...prev, [String(companyId)]: [] }));
    }
  };

  const toggleCompany = (companyId: Company['id']) => {
    setExpandedCompany(expandedCompany === companyId ? null : companyId);
  };

  const openSucursalForm = (companyId: Company['id']) => {
    if (!sucursalesSupported) {
      toast.error('Sucursales no disponibles en este entorno.');
      return;
    }
    setEditingCompanyId(companyId);
    setSucursalForm({ name: '' });
    setShowSucursalForm(true);
  };

  const handleAddSucursal = async () => {
    if (!editingCompanyId || !sucursalForm.name.trim()) return;

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/sucursales`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empresaId: editingCompanyId,
          name: sucursalForm.name
        })
      });

      if (response.ok) {
        await fetchSucursales(editingCompanyId);
        setShowSucursalForm(false);
        setSucursalForm({ name: '' });
      } else if (response.status === 404) {
        markSucursalesUnsupported();
        setSucursalesSupported(false);
        toast.error('Sucursales no disponibles en este entorno.');
      }
    } catch (error) {
      console.error('Error creating sucursal:', error);
    }
  };

  const handleDeleteSucursal = async (companyId: Company['id'], sucursalId: Sucursal['id']) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/sucursales/${String(sucursalId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchSucursales(companyId);
      } else if (response.status === 404) {
        markSucursalesUnsupported();
        setSucursalesSupported(false);
        toast.error('Sucursales no disponibles en este entorno.');
      }
    } catch (error) {
      console.error('Error deleting sucursal:', error);
    }
  };

  // Get current user from localStorage to check permissions
  const currentUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch {
      return null;
    }
  }, []);

  return (
    <ProtectedView 
      hasPermission={canManageCompanies(currentUser)}
      message="Solo el Controller General puede gestionar empresas y sucursales."
    >
      <div className="p-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Gestión de Empresas</h1>
            <p className="text-slate-400">Crea y administra empresas</p>
          </div>
          <button onClick={openNew} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-lg">Nueva Empresa</button>
        </div>

      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-slate-900 p-6 rounded-lg w-11/12 sm:w-96 mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">{editing ? 'Editar Empresa' : 'Nueva Empresa'}</h3>
            <div className="space-y-3">
              <input className="w-full p-2 rounded bg-slate-800 text-white" placeholder="Nombre" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
              <input className="w-full p-2 rounded bg-slate-800 text-white" placeholder="Identificador fiscal" value={form.cuit} onChange={e => setForm(prev => ({ ...prev, cuit: e.target.value }))} />
              <select className="w-full p-2 rounded bg-slate-800 text-white" value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value as any }))}>
                <option value="active">Activa</option>
                <option value="inactive">Inactiva</option>
              </select>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-slate-700 text-white rounded">Cancelar</button>
              <button onClick={() => {
                if (editing) updateCompany(editing.id, form);
                else addCompany(form);
                setShowForm(false);
              }} className="px-4 py-2 bg-sky-500 text-white rounded">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {showSucursalForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-slate-900 p-6 rounded-lg w-11/12 sm:w-96 mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Nueva Sucursal</h3>
            <div className="space-y-3">
              <input 
                className="w-full p-2 rounded bg-slate-800 text-white" 
                placeholder="Nombre de la sucursal" 
                value={sucursalForm.name} 
                onChange={e => setSucursalForm({ name: e.target.value })} 
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowSucursalForm(false)} className="px-4 py-2 bg-slate-700 text-white rounded">Cancelar</button>
              <button onClick={handleAddSucursal} className="px-4 py-2 bg-sky-500 text-white rounded">Guardar</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        {/* Mobile: stacked cards */}
        <div className="sm:hidden p-4 space-y-4">
          {companies.map(c => (
            <div key={c.id} className="bg-slate-900 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => toggleCompany(c.id)}
                        className="text-slate-400 hover:text-white"
                      >
                        {expandedCompany === c.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </button>
                      <h3 className="text-lg font-semibold text-white">{c.name}</h3>
                    </div>
                    <p className="text-slate-300 text-sm mt-1 ml-7">{c.cuit || '—'}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs ${c.status === 'active' ? 'bg-sky-500/20 text-sky-400' : 'bg-gray-500/20 text-gray-400'}`}>{c.status}</span>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => openEdit(c)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded">Editar</button>
                  <button onClick={() => setConfirmDeleteCompany({ isOpen: true, companyId: c.id })} className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 px-3 py-2 rounded">Eliminar</button>
                </div>
              </div>
              
              {expandedCompany === c.id && (
                <div className="bg-slate-950 p-4 border-t border-slate-700">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-white font-semibold flex items-center gap-2">
                      <Building2 size={16} />
                      Sucursales
                    </h4>
                    <button
                      onClick={() => openSucursalForm(c.id)}
                      className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={!sucursalesSupported}
                    >
                      <Plus size={16} />
                      Agregar
                    </button>
                  </div>
                  <div className="space-y-2">
                    {companySucursales[String(c.id)]?.length > 0 ? (
                      companySucursales[String(c.id)].map(s => (
                        <div key={s.id} className="flex justify-between items-center bg-slate-800 p-2 rounded">
                          <span className="text-slate-300 text-sm">{s.name}</span>
                          <button 
                            onClick={() => setConfirmDeleteSucursal({ isOpen: true, companyId: c.id, sucursalId: s.id })}
                            className="text-red-400 hover:text-red-300 text-xs"
                          >
                            Eliminar
                          </button>
                        </div>
                      ))
                    ) : sucursalesSupported ? (
                      <p className="text-slate-500 text-sm italic">Sin sucursales</p>
                    ) : (
                      <p className="text-amber-400 text-sm italic">Sucursales no disponibles.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop/table: visible on sm+ */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900">
              <tr>
                <th className="text-left px-6 py-4 text-slate-300 font-semibold w-12"></th>
                <th className="text-left px-6 py-4 text-slate-300 font-semibold">Nombre</th>
                <th className="text-left px-6 py-4 text-slate-300 font-semibold">Identificador Fiscal</th>
                <th className="text-left px-6 py-4 text-slate-300 font-semibold">Estado</th>
                <th className="text-left px-6 py-4 text-slate-300 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c, i) => (
                <React.Fragment key={c.id}>
                  <tr className={`border-t border-slate-700 ${i % 2 === 0 ? 'bg-slate-800' : 'bg-slate-800/50'}`}>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => toggleCompany(c.id)}
                        className="text-slate-400 hover:text-white"
                      >
                        {expandedCompany === c.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-white">{c.name}</td>
                    <td className="px-6 py-4 text-slate-300">{c.cuit || '—'}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${c.status === 'active' ? 'bg-sky-500/20 text-sky-400' : 'bg-gray-500/20 text-gray-400'}`}>{c.status}</span></td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(c)} className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded">Editar</button>
                        <button onClick={() => setConfirmDeleteCompany({ isOpen: true, companyId: c.id })} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-3 py-2 rounded">Eliminar</button>
                      </div>
                    </td>
                  </tr>
                  {expandedCompany === c.id && (
                    <tr className="bg-slate-950">
                      <td colSpan={5} className="px-6 py-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-white font-semibold flex items-center gap-2">
                            <Building2 size={18} />
                            Sucursales de {c.name}
                          </h4>
                          <button 
                            onClick={() => openSucursalForm(c.id)}
                            className="bg-sky-500 hover:bg-sky-600 text-white px-3 py-2 rounded text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={!sucursalesSupported}
                          >
                            <Plus size={16} />
                            Nueva Sucursal
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {companySucursales[String(c.id)]?.length > 0 ? (
                            companySucursales[String(c.id)].map(s => (
                              <div key={s.id} className="bg-slate-800 p-3 rounded flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <Building2 size={16} className="text-slate-400" />
                                  <span className="text-slate-200">{s.name}</span>
                                </div>
                                <button 
                                  onClick={() => setConfirmDeleteSucursal({ isOpen: true, companyId: c.id, sucursalId: s.id })}
                                  className="text-red-400 hover:text-red-300 text-sm"
                                >
                                  Eliminar
                                </button>
                              </div>
                            ))
                          ) : sucursalesSupported ? (
                            <p className="text-slate-500 italic col-span-full">No hay sucursales. Haz clic en "Nueva Sucursal" para agregar una.</p>
                          ) : (
                            <p className="text-amber-400 italic col-span-full">Sucursales no disponibles en este entorno.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDeleteCompany.isOpen}
        title="¿Eliminar empresa?"
        message="Esta acción no se puede deshacer. Se eliminarán todos los datos asociados a esta empresa, incluyendo usuarios y sucursales."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        onConfirm={() => {
          if (confirmDeleteCompany.companyId !== null) {
            deleteCompany(String(confirmDeleteCompany.companyId));
          }
        }}
        onCancel={() => setConfirmDeleteCompany({ isOpen: false, companyId: null })}
      />

      <ConfirmDialog
        isOpen={confirmDeleteSucursal.isOpen}
        title="¿Eliminar sucursal?"
        message="Esta acción no se puede deshacer. La sucursal será eliminada permanentemente."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        onConfirm={() => {
          if (confirmDeleteSucursal.companyId && confirmDeleteSucursal.sucursalId) {
            handleDeleteSucursal(confirmDeleteSucursal.companyId, confirmDeleteSucursal.sucursalId);
          }
        }}
        onCancel={() => setConfirmDeleteSucursal({ isOpen: false, companyId: null, sucursalId: null })}
      />
      </div>
    </ProtectedView>
  );
};

export default CompaniesView;
